import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { api, useSignalRNegotiateMutation } from "@webcore/services/api/api";
import { containerToTags, allWatchedTags } from "@webcore/services/signalr/containerTagMapping";
import { clientInstanceId } from "@webcore/services/clientInstanceId";

const DEBOUNCE_MS = 1500;

/**
 * Subscribes to SignalR data-change notifications and invalidates the matching RTK Query cache
 * tags. Shared across React apps (web-client, ops-mfe-app). The SignalR group the connection joins
 * is bound server-side to the session's active team; the teamId is supplied so the connection
 * re-negotiates when the active team changes.
 *
 * @param teamId The active team id. Both apps resolve this from their CoreApplicationContext, so it
 * is always present by the time this hook runs (the auth/bootstrap layer gates on a loaded context).
 */
export function useSignalR(teamId: string): void {
  const dispatch = useDispatch();
  const [negotiate] = useSignalRNegotiateMutation();
  const debounceTimerRef = useRef<number | null>(null);
  const pendingTagsRef = useRef<Set<string>>(new Set());

  const dataChanged_handler = useCallback((containerName: string, sourceClientInstanceId: string | null) => {
    if (sourceClientInstanceId === clientInstanceId) return;

    const tags = containerToTags[containerName];
    if (tags) {
      tags.forEach(t => pendingTagsRef.current.add(t));
    }

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = window.setTimeout(() => {
      const tagsToInvalidate = [...pendingTagsRef.current];
      pendingTagsRef.current.clear();
      debounceTimerRef.current = null;
      dispatch(api.util.invalidateTags(tagsToInvalidate as Parameters<typeof api.util.invalidateTags>[0]));
    }, DEBOUNCE_MS);
  }, [dispatch]);

  const reconnected_handler = useCallback(() => {
    dispatch(api.util.invalidateTags(allWatchedTags as Parameters<typeof api.util.invalidateTags>[0]));
  }, [dispatch]);

  useEffect(() => {
    if (!teamId) return;

    let cancelled = false;
    // The SignalR service is a global singleton. We capture the loaded module here so cleanup
    // can tear the connection down synchronously — relying on a deferred (microtask) disconnect
    // would race with the next effect's handler registration since disconnect() clears the
    // shared handler sets.
    let service: typeof import("@webcore/services/signalr/SignalRService") | null = null;
    let unsubDataChanged: (() => void) | null = null;
    let unsubReconnected: (() => void) | null = null;

    // Load the SignalR service lazily so the heavy @microsoft/signalr client is split out of
    // the initial app chunk. The real-time connection is non-critical at boot, so deferring it
    // by a tick has no functional impact.
    const connectSignalR = async () => {
      const loaded = await import("@webcore/services/signalr/SignalRService").catch(() => null);
      // Import failed (chunk unavailable) or the effect was torn down while loading — both are
      // non-fatal; the app works without real-time updates.
      if (loaded === null || cancelled) return;
      service = loaded;

      unsubDataChanged = loaded.onDataChanged(dataChanged_handler);
      unsubReconnected = loaded.onReconnected(reconnected_handler);

      try {
        const result = await negotiate().unwrap();
        if (cancelled) return;
        loaded.connect(result.url, result.accessToken);
      } catch {
        // SignalR connection failure is non-fatal — the app works without it
      }
    };

    connectSignalR();

    return () => {
      cancelled = true;
      unsubDataChanged?.();
      unsubReconnected?.();
      // `service` is set only after the module has loaded. If the import is still in flight,
      // `cancelled` prevents it from ever connecting, so there is nothing to disconnect.
      service?.disconnect();
      pendingTagsRef.current.clear();
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [teamId, negotiate, dataChanged_handler, reconnected_handler]);
}
